/**
 * SolenoidDetailV2.js
 * Route: /solenoid/:solenoid_id/:location_name
 * Replaces SolenoidDetailPage with Vaaripa design.
 *
 * API: GET /sensor/solenoid?solenoid_id=X
 * Returns: { data: { motor_id, sensor_id, sensor_online, updatedAt } }
 *
 * Source: PumpStatusV2 data via /sensor/v2/pump_status?motor_id=X
 * Destination: /sensor/data?sensor_id=X  (tank name + type)
 * Destination reading: /sensor/reading?sensor_id=X&type=water-level
 */

import axios from "axios";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { GlobalState } from "../../GlobalState";
import { formatDate, capitalizeFirstLetter } from "../utils/utils";
import { literToKiloliter } from "../utils/utils";
import PullToRefresh from "../utils/PullToRefresh";

/* ─── Palette ───────────────────────────────────────────────────────────────── */
const GREEN      = "#237A35";
const DARK_GREEN = "#0D3318";
const LT_GREEN   = "#F0FDF4";

/* ─── SVG Icons (inline — no external dependency) ───────────────────────────── */
const BorewellIcon = ({ size = 28, color = "#2563EB" }) => (
  <svg width={size} height={size} viewBox="0 0 30 30" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="20" x2="27" y2="20"/>
    <line x1="5" y1="22" x2="8" y2="25"/>
    <line x1="10" y1="22" x2="13" y2="25"/>
    <line x1="15" y1="22" x2="18" y2="25"/>
    <line x1="20" y1="22" x2="23" y2="25"/>
    <rect x="12" y="9" width="6" height="11" rx="1"/>
    <rect x="9" y="5" width="12" height="5" rx="2"/>
    <line x1="15" y1="9" x2="15" y2="5.5"/>
  </svg>
);

const PumpIcon = ({ size = 28, color = "#2563EB" }) => (
  <svg width={size} height={size} viewBox="0 0 30 30" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="15" cy="16" r="8"/>
    <line x1="3" y1="16" x2="7" y2="16"/>
    <line x1="15" y1="8" x2="15" y2="4"/>
    <line x1="15" y1="16" x2="18" y2="13"/>
    <line x1="15" y1="16" x2="18" y2="19"/>
    <line x1="15" y1="16" x2="12" y2="13"/>
    <line x1="15" y1="16" x2="12" y2="19"/>
    <rect x="23" y="13" width="5" height="6" rx="1"/>
  </svg>
);

const ValveIcon = ({ size = 28, color = GREEN }) => (
  <svg width={size} height={size} viewBox="0 0 30 30" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="15" x2="9" y2="15"/>
    <line x1="21" y1="15" x2="28" y2="15"/>
    <polygon points="9,10 9,20 15,15"/>
    <polygon points="21,10 21,20 15,15"/>
    <line x1="15" y1="10" x2="15" y2="6"/>
    <rect x="10" y="2" width="10" height="4" rx="1"/>
    <line x1="11" y1="3.5" x2="19" y2="3.5"/>
  </svg>
);

const SumpIcon = ({ size = 28, color = "#D97706" }) => (
  /* Underground sump: buried tank below ground, inlet pipe on top */
  <svg width={size} height={size} viewBox="0 0 30 30" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {/* Ground surface line */}
    <line x1="2" y1="9" x2="28" y2="9"/>
    {/* Inlet pipe going down from surface */}
    <line x1="15" y1="5" x2="15" y2="9"/>
    {/* Arrow down indicating underground */}
    <polyline points="12,7 15,9 18,7"/>
    {/* Underground tank body */}
    <rect x="5" y="9" width="20" height="15" rx="3"/>
    {/* Water level line inside tank */}
    <line x1="5" y1="19" x2="25" y2="19"/>
    {/* "U" shape at bottom to indicate depth */}
    <line x1="5" y1="19" x2="5" y2="24" strokeOpacity="0.4"/>
    <line x1="25" y1="19" x2="25" y2="24" strokeOpacity="0.4"/>
  </svg>
);

const OHTIcon = ({ size = 28, color = "#D97706" }) => (
  /* Overhead tank: round tank elevated on two pillars */
  <svg width={size} height={size} viewBox="0 0 30 30" fill="none"
    stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {/* Tank body — cylindrical shape */}
    <ellipse cx="15" cy="7" rx="9" ry="3"/>
    <line x1="6" y1="7" x2="6" y2="15"/>
    <line x1="24" y1="7" x2="24" y2="15"/>
    <ellipse cx="15" cy="15" rx="9" ry="3"/>
    {/* Water level inside */}
    <ellipse cx="15" cy="12" rx="9" ry="3" strokeDasharray="3 2"/>
    {/* Two support pillars */}
    <line x1="10" y1="18" x2="10" y2="28"/>
    <line x1="20" y1="18" x2="20" y2="28"/>
    {/* Base cross-beam */}
    <line x1="7" y1="28" x2="23" y2="28"/>
    {/* Outlet pipe at bottom */}
    <line x1="15" y1="18" x2="15" y2="28"/>
  </svg>
);

/* Pick source icon based on isBorewell flag */
function SourceIcon({ isBorewell, size = 28 }) {
  return isBorewell
    ? <BorewellIcon size={size} />
    : <PumpIcon size={size} />;
}

/* Pick destination icon based on sensor name / type */
function DestIcon({ name = "", size = 28 }) {
  const lower = name.toLowerCase();
  const isOHT  = lower.includes("oht") || lower.includes("overhead") || lower.includes("tower");
  return isOHT
    ? <OHTIcon size={size} />
    : <SumpIcon size={size} />;
}

/* ─── Styled Components ─────────────────────────────────────────────────────── */
const Screen = styled.div`
  background: #F4F6F9; min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;
`;

const Nav = styled.div`
  background: #fff; padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  border-bottom: 1px solid #EAECEF;
  position: sticky; top: 0; z-index: 20;
`;

const BackBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%;
  border: 1.5px solid #E5E7EB; background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; cursor: pointer; color: #374151;
  &:hover { border-color: ${GREEN}; color: ${GREEN}; }
`;

const NavInfo = styled.div` flex: 1; min-width: 0; `;
const NavEyebrow = styled.div` font-size: 11px; color: #4B5563; `;
const NavTitle = styled.div`
  font-size: 17px; font-weight: 700; color: #111;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const pulse = keyframes`0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)}`;

const OnlinePill = styled.div`
  display: flex; align-items: center; gap: 5px;
  padding: 5px 12px; border-radius: 20px;
  background: ${({ $on }) => $on ? "#DCFCE7" : "#F3F4F6"};
  border: 1px solid ${({ $on }) => $on ? "#BBF7D0" : "#E5E7EB"};
  font-size: 11px; font-weight: 600;
  color: ${({ $on }) => $on ? "#166534" : "#6B7280"};
`;

const PillDot = styled.div`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ $on }) => $on ? "#16A34A" : "#94A3B8"};
  ${({ $on }) => $on && css`animation: ${pulse} 2s infinite;`}
`;

/* Valve hero */
const ValveHero = styled.div`
  background: #fff; border-bottom: 1px solid #EAECEF; padding: 22px 18px 18px;
`;

const ValveStateLabel = styled.div`
  text-align: center; font-size: 11px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; color: #4B5563; margin-bottom: 12px;
`;

const PipeDiagram = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 0; margin: 0 auto; max-width: 360px;
`;

const PipeSegment = styled.div`
  height: 18px; flex: 1; background: #E5E7EB; border-radius: 2px;
  position: relative; overflow: hidden;
  ${({ $flowing }) => $flowing && css`
    background: #DBEAFE;
    &::after {
      content: '';
      position: absolute; top: 0; left: -100%; width: 60%; height: 100%;
      background: linear-gradient(90deg, transparent, rgba(59,130,246,.5), transparent);
      animation: flow 1.2s linear infinite;
    }
    @keyframes flow { to { left: 200%; } }
  `}
`;

const ValveBody = styled.div`
  width: 68px; height: 68px; border-radius: 50%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  flex-shrink: 0;
  border: 3px solid ${({ $open }) => $open ? GREEN : "#EF4444"};
  background: #fff;
  box-shadow: 0 0 0 6px ${({ $open }) => $open ? "rgba(35,122,53,.12)" : "rgba(239,68,68,.08)"},
              0 2px 12px rgba(0,0,0,0.08);
  transition: all .3s;
`;

const ValveStateText = styled.div`
  font-size: 9px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
  color: ${({ $open }) => $open ? GREEN : "#EF4444"}; margin-top: 2px;
`;

const ValveToggleRow = styled.div`
  display: flex; justify-content: center; margin-top: 14px; gap: 10px;
`;

const ValveBtn = styled.button`
  padding: 9px 24px; border-radius: 20px; border: none;
  font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer;
  background: ${({ $isOpen }) => $isOpen ? "#FEE2E2" : GREEN};
  color: ${({ $isOpen }) => $isOpen ? "#B91C1C" : "#fff"};
  transition: all .15s;
`;

const ValveSync = styled.div`
  text-align: center; font-size: 10px; color: #4B5563; margin-top: 8px;
`;

/* Connection strip */
const ConnectionSection = styled.div`
  background: #fff; margin-top: 10px;
  border-top: 1px solid #EAECEF; border-bottom: 1px solid #EAECEF;
  padding: 16px 18px;
`;

const ConnLabel = styled.div`
  font-size: 10px; font-weight: 700; color: #4B5563;
  letter-spacing: .08em; text-transform: uppercase; margin-bottom: 12px;
`;

const ConnRow = styled.div` display: flex; align-items: center; gap: 0; `;

const ConnNode = styled.div`
  flex: 1; background: #F9FAFB; border: 1.5px solid #E5E7EB;
  border-radius: 12px; padding: 12px 8px; text-align: center;
  cursor: ${({ $clickable }) => $clickable ? "pointer" : "default"};
  transition: all .15s;
  ${({ $active }) => $active && css`background: ${LT_GREEN}; border-color: ${GREEN};`}
  ${({ $clickable }) => $clickable && css`
    &:hover { border-color: ${GREEN}; background: ${LT_GREEN}; }
  `}
`;

const ConnIconWrap = styled.div`
  display: flex; align-items: center; justify-content: center; margin-bottom: 5px;
`;

const ConnType = styled.div`
  font-size: 9px; color: #4B5563; letter-spacing: .05em; text-transform: uppercase;
`;

const ConnName = styled.div`
  font-size: 11px; font-weight: 700; color: #111; margin: 2px 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const ConnStatus = styled.div`
  font-size: 10px; font-weight: 600;
  color: ${({ $green }) => $green ? "#16A34A" : "#9CA3AF"};
`;

const ConnArrow = styled.div`
  flex-shrink: 0; width: 32px; text-align: center;
  font-size: 18px; font-weight: 700; color: ${GREEN};
`;

/* Stats row */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr);
  background: #fff; border-bottom: 1px solid #EAECEF; margin-top: 10px;
`;

const StatItem = styled.div`
  padding: 13px 12px; text-align: center;
  border-right: 1px solid #F3F4F6;
  &:last-child { border-right: none; }
`;

const StatVal = styled.div`
  font-size: 16px; font-weight: 700; line-height: 1;
  color: ${({ $green }) => $green ? GREEN : "#111"};
`;

const StatLbl = styled.div`
  font-size: 9px; color: #4B5563; margin-top: 3px;
  letter-spacing: .04em; text-transform: uppercase;
`;

/* Operation log */









const LogTime  = styled.div` font-size: 10px; color: #4B5563; margin-top: 1px; `;


const LoadingBox = styled.div`
  padding: 40px; text-align: center; color: #4B5563; font-size: 14px;
`;

const Spacer = styled.div` height: 20px; background: #F4F6F9; `;

/* ─── Main Component ─────────────────────────────────────────────────────────── */
export default function SolenoidDetailV2() {
  const { solenoid_id, location_name } = useParams();
  const history = useHistory();
  const state = useContext(GlobalState);

  const [solenoidData,  setSolenoidData]  = useState(null);
  const [mappedData,    setMappedData]    = useState(null);   // solenoid-mapped
  const [sourceData,    setSourceData]    = useState(null);   // pump_status
  const [destData,      setDestData]      = useState(null);   // sensor reading
  const [destMeta,      setDestMeta]      = useState(null);   // sensor data (name, type)
  const [toggling,      setToggling]      = useState(false);  // valve toggle in progress
  const [loading, setLoading] = useState(true);

  /* ── Fetch solenoid detail ── */
  useEffect(() => {
    async function fetchAll() {
      if (!solenoid_id) return;
      try {
        const [res, mappedRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API}/sensor/solenoid?solenoid_id=${solenoid_id}`,
            { headers: { Authorization: state.token[0] } }
          ),
          axios.get(
            `${process.env.REACT_APP_API}/sensor/solenoid-mapped?solenoid_id=${solenoid_id}`,
            { headers: { Authorization: state.token[0] } }
          ).catch(() => ({ data: null })),
        ]);
        const d = res.data?.data;
        setSolenoidData(d);
        if (mappedRes.data) setMappedData(mappedRes.data?.data || mappedRes.data);

        /* Fetch source (pump/borewell) status */
        if (d?.motor_id) {
          try {
            const sr = await axios.get(
              `${process.env.REACT_APP_API}/sensor/v2/pump_status?motor_id=${d.motor_id}`,
              { headers: { Authorization: state.token[0] } }
            );
            setSourceData(sr.data);
          } catch { /* non-critical */ }
        }

        /* Fetch destination sensor metadata + live reading */
        if (d?.sensor_id) {
          try {
            const [metaRes, readRes] = await Promise.all([
              axios.get(
                `${process.env.REACT_APP_API}/sensor/data?sensor_id=${d.sensor_id}`,
                { headers: { Authorization: state.token[0] } }
              ),
              axios.get(
                `${process.env.REACT_APP_API}/sensor/reading?sensor_id=${d.sensor_id}&&type=water-level`,
                { headers: { Authorization: state.token[0] } }
              ),
            ]);
            setDestMeta(metaRes.data?.data || metaRes.data);
            setDestData(readRes.data);
          } catch { /* non-critical */ }
        }
      } catch (e) {
        console.error("solenoid fetch:", e);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
    const iv = setInterval(fetchAll, 15000);
    return () => clearInterval(iv);
  }, [solenoid_id, state.token]);

  /* ── Derived values ── */
  const isOnline    = !!solenoidData?.sensor_online;
  // valve_on: "1" = open, "0" = closed (API returns string)
  const isOpen      = solenoidData?.valve_on === "1" ||
                      solenoidData?.valve_on === 1 ||
                      solenoidData?.valve_on === true;
  const valveName   = location_name || solenoidData?.location_name || "Solenoid Valve";

  /* Source — prefer mappedData, fall back to pump_status */
  const ms          = sourceData?.motor_status || {};
  const isBorewell  = !!(mappedData?.isBorewell ?? ms.isBorewell);
  const sourceName  = mappedData?.motor_location || ms.location_name || (isBorewell ? "Borewell" : "Pump");
  const sourceOn    = ms.motor_on === 1 || ms.motor_on === true || ms.motor_on === "1";
  const sourceOnline = sourceData?.sensor_status === "online";

  /* Destination — prefer mappedData, fall back to sensor reading */
  const destName    = mappedData?.sensor_location || destMeta?.location_name || destMeta?.location || "Tank";
  const destPct     = parseFloat(destData?.volume || mappedData?.tank_percentage || 0);
  const destVol     = literToKiloliter(parseFloat(destData?.value || mappedData?.tank_volume || 0));
  const destOnline  = destData?.sensor_status === "online";

  /* Sync time */
  const syncTime    = solenoidData?.updatedAt ? formatDate(solenoidData.updatedAt) : "—";

  /* ── Toggle valve open/close ── */
  const handleToggle = async () => {
    if (toggling || !isOnline) return;
    setToggling(true);
    try {
      const newState = isOpen ? 0 : 1;
      // solenoid_id in query string, valve_on as string "1"/"0" — matches SolenoidBox.js
      const response = await axios.patch(
        `${process.env.REACT_APP_API}/sensor/solenoid?solenoid_id=${solenoid_id}`,
        { valve_on: newState === 1 ? "1" : "0" },
        { headers: { Authorization: state.token[0] } }
      );
      // Refresh data after successful toggle
      if (response.data) {
        setSolenoidData(prev => ({ ...prev, valve_on: newState === 1 ? "1" : "0" }));
      }
    } catch (e) {
      console.error("valve toggle failed:", e);
      alert("Failed to toggle valve. Please try again.");
    } finally {
      setToggling(false);
    }
  };

  /* ── Export as PNG (captures full page including header + footer) ── */
  const screenRef = React.useRef(null);

  const handleDownload = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;

      // Capture full document body — includes app Header (logo) and Footer
      const canvas = await html2canvas(document.body, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#F4F6F9",
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,   // capture from top of page
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        ignoreElements: (el) =>
          el.classList?.contains("no-print") ||
          el.id === "vaaripa-print-logo",
      });

      const dataUrl = canvas.toDataURL("image/png");

      // Mobile: Web Share API opens native share sheet (WhatsApp, Telegram etc.)
      if (navigator.share && navigator.canShare) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], "vaaripa-report.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "Vaaripa Report" });
            return;
          }
        } catch { /* fall through to download */ }
      }

      // Desktop: auto-download to Downloads folder
      const link = document.createElement("a");
      link.download = `vaaripa-report-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error("Screenshot failed:", e);
      alert("Could not generate image. Please try again.");
    }
  };

  if (loading) return <LoadingBox>Loading valve details…</LoadingBox>;

  return (
    <Screen>
      <PullToRefresh />
      {/* NAV */}
      <Nav data-ptr-header>
        <BackBtn onClick={() => history.goBack()}>←</BackBtn>
        <NavInfo>
          <NavEyebrow>Solenoid valve</NavEyebrow>
          <NavTitle title={valveName}>{valveName}</NavTitle>
        </NavInfo>
        <OnlinePill $on={isOnline}>
          <PillDot $on={isOnline} />
          {isOnline ? "Online" : "Offline"}
        </OnlinePill>
      </Nav>

      {/* VALVE HERO */}
      <ValveHero>
        <ValveStateLabel>Valve status</ValveStateLabel>

        <PipeDiagram>
          <PipeSegment $flowing={isOpen && isOnline} />
          <ValveBody $open={isOpen}>
            <ValveIcon size={26} color={isOpen ? GREEN : "#EF4444"} />
            <ValveStateText $open={isOpen}>{isOpen ? "Open" : "Closed"}</ValveStateText>
          </ValveBody>
          <PipeSegment $flowing={isOpen && isOnline} />
        </PipeDiagram>

        <ValveToggleRow>
          <ValveBtn
            $isOpen={isOpen}
            onClick={handleToggle}
            disabled={toggling || !isOnline}
            style={{ opacity: toggling ? 0.6 : 1 }}
          >
            {toggling ? "Please wait…" : isOpen ? "Close Valve" : "Open Valve"}
          </ValveBtn>
        </ValveToggleRow>

        <ValveSync>Synced {syncTime}</ValveSync>
      </ValveHero>

      {/* CONNECTION FLOW */}
      <ConnectionSection>
        <ConnLabel>Connection flow</ConnLabel>
        <ConnRow>

          {/* Source */}
          <ConnNode
            $clickable={!!sourceData}
            onClick={() => {
              if (solenoidData?.motor_id) {
                history.push(`/motor/${solenoidData.motor_id}/sensor/${solenoidData.motor_id}/Pump`);
              }
            }}
          >
            <ConnIconWrap>
              <SourceIcon isBorewell={isBorewell} size={28} />
            </ConnIconWrap>
            <ConnType>Source</ConnType>
            <ConnName title={sourceName}>{sourceName}</ConnName>
            <ConnStatus $green={sourceOn && sourceOnline}>
              {!sourceOnline ? "Offline" : sourceOn ? "ON · running" : "OFF"}
            </ConnStatus>
          </ConnNode>

          <ConnArrow>→</ConnArrow>

          {/* This valve */}
          <ConnNode $active>
            <ConnIconWrap>
              <ValveIcon size={28} color={isOpen ? GREEN : "#EF4444"} />
            </ConnIconWrap>
            <ConnType>Valve</ConnType>
            <ConnName title={valveName}>{valveName}</ConnName>
            <ConnStatus $green={isOpen && isOnline}>
              {!isOnline ? "Offline" : isOpen ? "Open" : "Closed"}
            </ConnStatus>
          </ConnNode>

          <ConnArrow>→</ConnArrow>

          {/* Destination */}
          <ConnNode
            $clickable={!!destData}
            onClick={() => {
              if (solenoidData?.sensor_id) {
                history.push(`/sensor/${solenoidData.sensor_id}/Water Level`);
              }
            }}
          >
            <ConnIconWrap>
              <DestIcon name={destName} size={28} />
            </ConnIconWrap>
            <ConnType>Destination</ConnType>
            <ConnName title={destName}>{destName}</ConnName>
            <ConnStatus $green={destOnline}>
              {!destOnline ? "Offline" : `${Math.round(destPct)}% · ${destVol}`}
            </ConnStatus>
          </ConnNode>

        </ConnRow>
      </ConnectionSection>

      {/* STATS */}
      <StatsRow>
        <StatItem>
          <StatVal $green>{isOnline ? "Online" : "Offline"}</StatVal>
          <StatLbl>Current status</StatLbl>
        </StatItem>
        <StatItem>
          <StatVal>{syncTime}</StatVal>
          <StatLbl>Last synced</StatLbl>
        </StatItem>
      </StatsRow>

      {/* Operation log: no backend history endpoint — omitted */}

      <Spacer />
    </Screen>
  );
}